import { CascadingFilter } from '@app/components/data-table/cascading-filter';
import { toTaxonomyFilterNodes } from '@app/purchased-goods/components/taxonomy-filter-nodes';
import { useAnalytics } from '@app/services/analytics/AnalyticsContext';
import { useGetPurchasedGoodsTaxonomy } from '@shared/api/hooks/purchased-goods/taxonomy';
import { useMemo } from 'react';
import {
  INSIGHTS_TAXONOMY_TIERS,
  isTerminalTaxonomyPath,
  useInsightsExplore,
  useInsightsTableState,
  useInsightsTaxonomy,
  useInsightsTaxonomyScope,
} from '../hooks/use-insights-filters';

/**
 * The product taxonomy filter — one pill covering all three tiers, offered as
 * the tree of what the organisation actually bought under the current site,
 * supplier and period.
 *
 * Picking a base product, or the uncategorised bucket, also moves the
 * breakdown to items: nothing in the taxonomy sits below either, so the level
 * beneath is the goods themselves.
 */
export function InsightsTaxonomyFilter() {
  const { path, selectPath, clear } = useInsightsTaxonomy();
  const [explore, setExplore] = useInsightsExplore();
  const [, setTableState] = useInsightsTableState();
  const scope = useInsightsTaxonomyScope();
  const { data: categories, isLoading } = useGetPurchasedGoodsTaxonomy(scope);
  const { track } = useAnalytics();

  const nodes = useMemo(() => toTaxonomyFilterNodes(categories), [categories]);

  const onSelect = (next: string[]) => {
    selectPath(next);
    setTableState({ page: null });
    // A chosen base product or the uncategorised bucket leaves nothing below
    // it in the taxonomy, so the breakdown continues into the items it covers.
    if (isTerminalTaxonomyPath(next) && explore === 'category') {
      setExplore('item');
    }
    track('Applied PG Insights Filter', {
      filter_type:
        INSIGHTS_TAXONOMY_TIERS[next.length - 1] ?? 'productCategory',
      value_names: next.slice(-1),
      value_count: next.length ? 1 : 0,
    });
  };

  return (
    <CascadingFilter
      title="Category"
      nodes={nodes}
      selectedPath={path}
      isLoading={isLoading}
      rootHeading="Categories"
      clearLabel="All categories"
      emptyMessage="No categorised purchases"
      onSelect={onSelect}
      onClear={() => {
        clear();
        setTableState({ page: null });
        track('Applied PG Insights Filter', {
          filter_type: 'productCategory',
          value_names: [],
          value_count: 0,
        });
      }}
    />
  );
}
